import { Injectable } from "@angular/core";
import { PP_QUERY_LIMIT } from "@modules/picking-points/services/picking-points.service";
import { Observable, of } from "rxjs";
import { catchError, expand, finalize, map, mergeMap, reduce, shareReplay, takeWhile } from "rxjs/operators";

export const PP_CACHE_TTL_MS = 5 * 60 * 1000;
// Only cache when the account has enough picking points to make caching worthwhile
export const PP_CACHE_MIN_SIZE = 150;

export type PickingPointPageFetcher = (limit: number, offset: number) => Observable<any[]>;
export type PickingPointChecksumToken = { count: number; max_updated_at: string | null };
export type PickingPointChecksumFetcher = () => Observable<PickingPointChecksumToken>;

interface PickingPointCacheEntry {
  data: any[];
  checksumToken: PickingPointChecksumToken;
  fetchedAt: number;
}

@Injectable({
  providedIn: "root",
})
export class PickingPointCacheService {
  private cache = new Map<string, PickingPointCacheEntry>();
  private inflight = new Map<string, Observable<any[]>>();

  /**
   * Returns the full picking point list for the given query params, paginating with
   * `fetchPage` only when needed. Within the TTL the cached list is served without any
   * HTTP call. After the TTL a single cheap checksum probe (`checksumFetcher`) compares
   * `count` + `max_updated_at` against the stored token before deciding whether to reuse
   * the cache or trigger a full refetch.
   */
  public getPickingPoints(
    keyParams: unknown,
    fetchPage: PickingPointPageFetcher,
    checksumFetcher: PickingPointChecksumFetcher
  ): Observable<any[]> {
    const key = JSON.stringify(keyParams);
    const entry = this.cache.get(key);
    const age = entry ? Date.now() - entry.fetchedAt : null;

    if (entry && age !== null && age < PP_CACHE_TTL_MS) {
      return of([...entry.data]);
    }

    const pending = this.inflight.get(key);
    if (pending) {
      return pending;
    }

    const request$ = (
      entry ? this.revalidate(key, entry, checksumFetcher, fetchPage) : this.fetchAll(key, fetchPage, checksumFetcher)
    ).pipe(
      finalize(() => this.inflight.delete(key)),
      shareReplay(1)
    );
    this.inflight.set(key, request$);
    return request$;
  }

  /** Drops every cached list and all in-flight requests. Call after any local picking point mutation. */
  public invalidate(): void {
    this.cache.clear();
    this.inflight.clear();
  }

  private revalidate(
    key: string,
    entry: PickingPointCacheEntry,
    checksumFetcher: PickingPointChecksumFetcher,
    fetchPage: PickingPointPageFetcher
  ): Observable<any[]> {
    return checksumFetcher().pipe(
      mergeMap(liveToken => {
        const unchanged = liveToken.count === entry.checksumToken.count && liveToken.max_updated_at === entry.checksumToken.max_updated_at;
        if (unchanged) {
          entry.checksumToken = liveToken;
          entry.fetchedAt = Date.now();
          return of([...entry.data]);
        }
        return this.fetchAll(key, fetchPage, checksumFetcher);
      }),
      catchError(err => {
        console.error("[PPCache] checksum probe failed, falling back to full fetch", err);
        return this.fetchAll(key, fetchPage, checksumFetcher);
      })
    );
  }

  private fetchAll(key: string, fetchPage: PickingPointPageFetcher, checksumFetcher: PickingPointChecksumFetcher): Observable<any[]> {
    return of({ done: false, offset: 0, pp: [] as any[] }).pipe(
      expand(state =>
        fetchPage(PP_QUERY_LIMIT, state.offset).pipe(
          map(page => {
            return {
              pp: [...state.pp, ...page],
              done: page.length < PP_QUERY_LIMIT,
              offset: state.offset + page.length,
            };
          })
        )
      ),
      takeWhile(state => !state.done, true),
      reduce((_acc, state) => state.pp, [] as any[]),
      mergeMap(pp => {
        if (pp.length <= PP_CACHE_MIN_SIZE) {
          return of(pp);
        }
        return checksumFetcher().pipe(
          map(checksumToken => {
            const newEntry: PickingPointCacheEntry = { data: [...pp], checksumToken, fetchedAt: Date.now() };
            this.cache.set(key, newEntry);
            return pp;
          }),
          catchError(err => {
            // Checksum unavailable — store with a sentinel null token so next revalidation always fetches fresh
            console.error("[PPCache] failed to get initial checksum token, caching without token", err);
            const fallbackEntry: PickingPointCacheEntry = {
              data: [...pp],
              checksumToken: { count: pp.length, max_updated_at: null },
              fetchedAt: Date.now(),
            };
            this.cache.set(key, fallbackEntry);
            return of(pp);
          })
        );
      })
    );
  }
}
